import React, { useState } from 'react';
import { getSourceDisplayName, getSourceUrl, getFaultCodeReferenceLinks } from '../utils/sourceRegistry';
import DocViewerModal from './DocViewerModal';

function EscalationSummary({ escalation, ticket, onNewTicket, showNewTicketButton = true }) {
  const [docViewer, setDocViewer] = useState({ open: false, url: '', title: '' });
  if (!escalation) return null;

  const urgencyClass =
    escalation.urgency === 'CRITICAL' ? 'high' :
    escalation.urgency === 'URGENT' ? 'medium' : 'low';

  const renderMarkdown = (text) => {
    const rawText = text || "";
    try {
      if (window.marked) {
        return { __html: window.marked.parse(rawText) };
      }
    } catch (e) {
      console.error("Markdown Error", e);
    }
    return { __html: rawText };
  };

  return (
    <div className="escalation-summary">
      <h2 className="escalation-summary__title">Escalation Summary</h2>
      <p className="escalation-summary__subtitle">
        This has been sent to your supervisor for review
      </p>

      <div className="card">
        {/* Summary in its own box */}
        <div
          className="escalation-summary__summary-box"
          dangerouslySetInnerHTML={renderMarkdown(escalation.escalation_summary)}
        />

        {/* Recommended Action */}
        <div className="escalation-summary__box">
          <div className="escalation-summary__label">Recommended Action</div>
          <div
            className="escalation-summary__value"
            dangerouslySetInnerHTML={renderMarkdown(escalation.recommended_action)}
          />
        </div>

        {/* Estimated Cost */}
        {escalation.estimated_cost_range && (
          <div className="escalation-summary__box">
            <div className="escalation-summary__label">Estimated Cost</div>
            <div className={`escalation-summary__value escalation-summary__value--mono`}>
              {escalation.estimated_cost_range}
            </div>
          </div>
        )}

        {/* Warranty */}
        {escalation.warranty_applicable && (
          <div className="escalation-summary__box">
            <div className="escalation-summary__label">Warranty</div>
            <div className="escalation-summary__value">
              <span
                className={`badge badge--${
                  escalation.warranty_applicable === 'YES'
                    ? 'low'
                    : escalation.warranty_applicable === 'POSSIBLY'
                    ? 'medium'
                    : 'info'
                }`}
              >
                {escalation.warranty_applicable}
              </span>
              {escalation.warranty_reasoning && (
                <div
                  className="escalation-summary__warranty-reasoning"
                  dangerouslySetInnerHTML={renderMarkdown(escalation.warranty_reasoning)}
                />
              )}
            </div>
          </div>
        )}

        {/* Urgency */}
        <div className="escalation-summary__box">
          <div className="escalation-summary__label">Urgency</div>
          <div className="escalation-summary__value">
            <span className={`badge badge--${urgencyClass}`}>
              {escalation.urgency}
            </span>
          </div>
        </div>

        {/* AI Confidence */}
        {escalation.confidence > 0 && (
          <div className="escalation-summary__box">
            <div className="escalation-summary__label">AI Confidence</div>
            <div className="escalation-summary__confidence-wrap">
              <div className="confidence-meter">
                <div className="confidence-meter__bar">
                  <div
                    className="confidence-meter__fill"
                    style={{
                      width: `${escalation.confidence * 100}%`,
                      background:
                        escalation.confidence > 0.7
                          ? 'var(--color-safe)'
                          : 'var(--color-warning)',
                    }}
                  />
                </div>
                <span className="confidence-meter__label">
                  {(escalation.confidence * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Reasoning (if available) */}
        {escalation.reasoning && (
          <div className="escalation-summary__box">
            <div className="escalation-summary__label">Internal Reasoning</div>
            <div
              className="escalation-summary__value"
              dangerouslySetInnerHTML={renderMarkdown(escalation.reasoning)}
            />
          </div>
        )}

        {/* Sources — RAG documents used, with friendly names and links */}
        {escalation.sources && escalation.sources.length > 0 && (
          <div className="escalation-summary__box">
            <div className="escalation-summary__label">Knowledge base used (RAG)</div>
            <p className="escalation-summary__sources-hint">Specific documents used for this escalation. Click to open:</p>
            <ul className="escalation-summary__sources rag-sources-list" aria-label="RAG source documents">
              {escalation.sources.map((src, idx) => {
                const filename = typeof src === 'string' ? src.trim() : String(src);
                const displayName = getSourceDisplayName(filename);
                const href = getSourceUrl(filename);
                return (
                  <li key={idx} className="escalation-summary__source-tag rag-source-item">
                    <span className="rag-source-item__name">{displayName}</span>
                    <button
                      type="button"
                      className="escalation-summary__source-link rag-source-item__link"
                      onClick={() => setDocViewer({ open: true, url: href, title: displayName })}
                      title={`View ${displayName}`}
                    >
                      View document →
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Fault code reference — external links (Autocodes, Engine-codes) */}
        {ticket && ticket.fault_code && getFaultCodeReferenceLinks(ticket.fault_code).length > 0 && (
          <div className="escalation-summary__box">
            <div className="escalation-summary__label">Fault code reference</div>
            <p className="escalation-summary__sources-hint">
              Look up <strong>{ticket.fault_code}</strong> on external sites:
            </p>
            <ul className="escalation-summary__sources rag-sources-list" aria-label="Fault code links">
              {getFaultCodeReferenceLinks(ticket.fault_code).map((link, idx) => (
                <li key={idx} className="escalation-summary__source-tag rag-source-item">
                  <span className="rag-source-item__name">{link.name}</span>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="escalation-summary__source-link rag-source-item__link" title={`Open ${link.name}`}>
                    Open →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DocViewerModal
          isOpen={docViewer.open}
          url={docViewer.url}
          title={docViewer.title}
          onClose={() => setDocViewer({ open: false, url: '', title: '' })}
        />

        {/* Safety warning */}
        {escalation.safety_implications && (
          <div className="escalation-summary__safety-box">
            <strong>▲ Safety implications:</strong>{' '}
            <span dangerouslySetInnerHTML={renderMarkdown(escalation.safety_detail || '')} />
          </div>
        )}

        {/* Evidence photos collected */}
        {ticket && (() => {
          const raw = ticket.evidence_results_json;
          if (!raw) return null;
          let results;
          try {
            results = typeof raw === 'string' ? JSON.parse(raw) : raw;
          } catch {
            return null;
          }
          if (!Array.isArray(results) || results.length === 0) return null;
          const stepsWithPhotos = results.filter(
            (step) => step.photo_base64_list && step.photo_base64_list.length > 0
          );
          if (stepsWithPhotos.length === 0) return null;
          return (
            <div className="escalation-summary__box escalation-summary__evidence-photos">
              <div className="escalation-summary__label">Evidence photos collected</div>
              {stepsWithPhotos.map((step, idx) => (
                <div key={idx} className="evidence-photos-step">
                  <div className="evidence-photos-step__label">
                    Step {step.step_number}: {step.action}
                  </div>
                  <div className="photo-thread">
                    {step.photo_base64_list.map((dataUrl, i) => (
                      <img
                        key={i}
                        src={dataUrl}
                        alt={`Step ${step.step_number} photo ${i + 1}`}
                        className="photo-thread__img"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Awaiting approval */}
        <div className="escalation-summary__approval-box">
          <div className="escalation-summary__approval-title">
            Awaiting Supervisor Approval
          </div>
          <div className="escalation-summary__approval-note">
            You'll be notified when a decision is made
          </div>
        </div>

        {showNewTicketButton && (
          <button className="btn btn--outline btn--block" onClick={onNewTicket}>
            Start New Ticket
          </button>
        )}
      </div>
    </div>
  );
}

export default EscalationSummary;
